import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { bindOnrampCache } from '../operations/cache-lifecycle.js';
import { fundingContextQuery } from './context-query.js';
import { resolveDeviceMethods } from './device-methods.js';
import { discoverFundingCheckout } from './discover-checkout.js';
export function useFundingDiscovery(options) {
  const $ = _c(30);
  const {
    client,
    currency,
    surface,
    deviceMethods,
    manifest
  } = options;
  const cache = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== cache || $[1] !== client) {
    t0 = () => {
      bindOnrampCache(client, cache);
    };
    t1 = [client, cache];
    $[0] = cache;
    $[1] = client;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  const t2 = client.cacheKey;
  const t3 = options.sessionProofId;
  const t4 = options.resumeOperationId === undefined ? "auto-resume" : options.resumeOperationId;
  const t5 = options.directPurchase?.binding.caip19 ?? null;
  const t6 = Boolean(options.directPurchase && options.directPurchaseEnabled === true);
  const t7 = options.authenticationRequested ?? false;
  const t8 = options.sandboxIgnoreLocationRestrictions ?? false;
  let t9;
  if ($[4] !== deviceMethods) {
    t9 = resolveDeviceMethods(deviceMethods, "sandbox");
    $[4] = deviceMethods;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  let t10;
  if ($[6] !== deviceMethods) {
    t10 = resolveDeviceMethods(deviceMethods, "production");
    $[6] = deviceMethods;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== t10 || $[9] !== t9) {
    t11 = {
      sandbox: t9,
      production: t10
    };
    $[8] = t10;
    $[9] = t9;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== client.cacheKey || $[12] !== currency || $[13] !== manifest || $[14] !== options.sessionProofId || $[15] !== surface || $[16] !== t11 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t12 = ["onramp", t2, "discovery", t3, t4, t5, t6, t7, t8, currency, surface, t11, manifest];
    $[11] = client.cacheKey;
    $[12] = currency;
    $[13] = manifest;
    $[14] = options.sessionProofId;
    $[15] = surface;
    $[16] = t11;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      persist: false
    };
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== cache || $[25] !== options) {
    t14 = t15 => {
      const {
        signal
      } = t15;
      return discoverWithinDeadline(options, cache, signal);
    };
    $[24] = cache;
    $[25] = options;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t12 || $[28] !== t14) {
    t15 = {
      queryKey: t12,
      staleTime: Infinity,
      gcTime: Infinity,
      networkMode: "always",
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      meta: t13,
      queryFn: t14
    };
    $[27] = t12;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const discovery = useQuery(t15);
  return discovery;
}
async function discoverWithinDeadline(options, cache, signal) {
  const controller = new AbortController();
  let expired = false;
  const cancel = () => controller.abort();
  const stopped = new Promise((_, reject) => {
    controller.signal.addEventListener('abort', () => {
      const error = new Error(expired ? 'onramp_discovery_timeout' : 'onramp_discovery_aborted');
      if (!expired) error.name = 'AbortError';
      reject(error);
    }, {
      once: true
    });
  });
  signal.addEventListener('abort', cancel, {
    once: true
  });
  // Native provider HTTP methods have no deadline; a stalled request must expose retry.
  const timer = setTimeout(() => {
    expired = true;
    cancel();
  }, 30_000);
  try {
    if (signal.aborted) cancel();
    const work = async () => {
      const context = await cache.fetchQuery(fundingContextQuery(options.client));
      return discoverFundingCheckout(options, controller.signal, context);
    };
    return await Promise.race([work(), stopped]);
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', cancel);
  }
}